import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import cn from 'classnames';
import StudentAttendanceDetails from 'components/Admin/StudentAttendanceDetails';
import { Button } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { apiErrorHandler } from 'core/errors';
import { API, getCancelToken, request } from 'redux/api';
import { RootState } from 'redux/reducers';

import { ReactComponent as ChevronRightIcon } from 'assets/chevron_right.svg';

import ExportAllSessionAttendanceDialog from './ExportAllSessionAttendanceDialog';

interface GetAllStudentAttendancesResponse extends MessageResponse {
  studentsData: StudentCourseAverageAttendance[];
}

interface StudentAttendanceProps extends ToastManagerProp {
  getAccess: SessionData | null;
}

interface StudentAttendanceState {
  isLoading: boolean;
  students: StudentCourseAverageAttendance[];
  showStudentDetails: boolean;
  studentBeingViewed: StudentCourseAverageAttendance | null;
  isExportAllSessionAttendanceDialogOpen: boolean;
}

class StudentAttendance extends Component<StudentAttendanceProps, StudentAttendanceState> {
  apiSources = getCancelToken();

  state: StudentAttendanceState = {
    students: [],
    isLoading: false,
    showStudentDetails: false,
    studentBeingViewed: null,
    isExportAllSessionAttendanceDialogOpen: false,
  };

  async getAllStudentAttendances(courseId: string) {
    const { studentsData } = await request<GetAllStudentAttendancesResponse>({
      url: API.ATTENDANCE_COURSE_ALL_STUDENTS,
      params: { courseId },
      cancelToken: this.apiSources.token,
    });
    return studentsData;
  }

  async componentDidMount() {
    this.apiSources = getCancelToken();
    try {
      if (this.props.getAccess?.courseDetails.courseId) {
        this.setState({ isLoading: true });
        const students = await this.getAllStudentAttendances(this.props.getAccess.courseDetails.courseId);
        this.setState({ students, isLoading: false });
      }
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  getRow = (student: StudentCourseAverageAttendance) => {
    return (
      <tr
        key={student.userId}
        className={cn({ hovered: student.isActive, clickable: student.isActive })}
        onClick={() => this.showStudentDetails(student)}
      >
        <td className='table__cell'>
          <div className='table__cell-header table__cell-header--title'>{student.name}</div>
          <div className='table__cell-value'>{student.emailId}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>LAST ACTIVE ON</div>
          <div
            className={cn('table__cell-value', 'table__cell-value--no-wrap', {
              'table__cell-value--red': !student.isActive,
            })}
          >
            {student.isActive ? dt.format(student.lastActive, 'do MMM yyyy') : "Hasn't used Acadly"}
          </div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>AVERAGE ATTENDANCE</div>
          {student.isActive ? (
            <div className={cn('table__cell-value', getColorClassForPercentage(student.averageAttendance))}>
              {student.averageAttendance}%
            </div>
          ) : (
            <div className='table__cell-value'>--</div>
          )}
        </td>
        <td className={cn('table__cell', { 'table__cell--more': student.isActive })}>
          {student.isActive ? <ChevronRightIcon display='block' /> : null}
        </td>
      </tr>
    );
  };

  showStudentDetails = (student: StudentCourseAverageAttendance) => {
    if (!student.isActive) return;
    this.setState({
      showStudentDetails: true,
      studentBeingViewed: student,
    });
  };

  hideStudentDetails = () => {
    this.setState({
      showStudentDetails: false,
    });
  };

  handleOpenExportAllSessionAttendanceDialog = () => {
    this.setState({
      isExportAllSessionAttendanceDialogOpen: true,
    });
  };

  handleCloseExportAllSessionAttendanceDialog = () => {
    this.setState({
      isExportAllSessionAttendanceDialogOpen: false,
    });
  };

  render() {
    const { isLoading, students, studentBeingViewed, showStudentDetails } = this.state;
    return (
      <div className='section'>
        <div className='section__header'>
          <h3 className='section__title'>
            {isLoading
              ? 'Loading attendance...'
              : !students.length
              ? 'No attendance recorded'
              : `${students.length || 'No'} enrolled student${students.length === 1 ? '' : 's'}`}
          </h3>
          <Button
            disabled={students.length === 0}
            label='Export'
            type='ghost'
            onRipple={this.handleOpenExportAllSessionAttendanceDialog}
          />
        </div>
        <div className='section__body'>
          <table className='table table--bordered student-attendance-table'>
            <tbody>{students.map(this.getRow)}</tbody>
          </table>
        </div>
        <StudentAttendanceDetails
          isOpen={showStudentDetails}
          student={studentBeingViewed}
          onClose={this.hideStudentDetails}
        />
        <ExportAllSessionAttendanceDialog
          isOpen={this.state.isExportAllSessionAttendanceDialogOpen}
          onClose={this.handleCloseExportAllSessionAttendanceDialog}
        />
      </div>
    );
  }
}

/**
 * Returns a color class name for a number between 0 and 100, returns empty string otherwise
 * @param percentage attendance in percentage
 */
const getColorClassForPercentage = (percentage: number) => {
  if (percentage >= 80) return 'table__cell-value--green';
  else if (percentage >= 60) return 'table__cell-value--orange';
  return 'table__cell-value--red';
};

const mapStateToProps = (state: RootState) => {
  return {
    getAccess: state.getAccess,
  };
};

export default connect(mapStateToProps)(withToastManager(StudentAttendance));
